import { of } from 'rxjs/internal/observable/of';
import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, EndSessionRequest, IPublicClientApplication, RedirectRequest, stubbedPublicClientApplication } from '@azure/msal-browser';
import { Observable } from 'rxjs/internal/Observable';
import { environment } from '../../../environments/environment';
import { UsersBus } from '@app/cp/shared/models/UsersBus.model';
import { AppSettings } from '@app/cp/shared/models/AppSettings';
import { JwtHelperService } from "@auth0/angular-jwt";
import { ReplaySubject, Subject, takeUntil } from 'rxjs';
import { CommonValues } from '@app/cp/shared/static/CommonValues';
import { NgxIndexedDBService } from 'ngx-indexed-db';

const defaultUser = new UsersBus;
defaultUser.username = 'guest@climatepros.com';

const mockUserAccountInfo: AccountInfo = {
    username: 'guest@climatepros.com', //email,
    name: 'Awesome FieldJetX' //name
} as AccountInfo;

@Injectable()
export class CmProAuthService {

    private unsubscribeSubjects: Map<string, Subject<void>> = new Map();

    instance: IPublicClientApplication;

    accountInfo: AccountInfo;

    user: UsersBus;

    /**
     * Allows subscribing to userLoggedIn event. This should only be used externally by the USerLoadedResolver. All other use cases should use subscribeToLoginEvents
     *
     * @memberof CmProAuthService
     */
    private userLoggedInEvent = new ReplaySubject<UsersBus>(1);

    userLoggedInEvent$ = this.userLoggedInEvent.asObservable();

    isLoggedIn(): boolean {
        return this.user && this.user.username != defaultUser.username && this.isEntraLoggedIn();
    }

    isEntraLoggedIn(): boolean {
        return this.instance?.getAllAccounts().length > 0;
    }

    private useMock: boolean;

    constructor(private msalService: MsalService, private dbService: NgxIndexedDBService) {
        this.useMock = !!environment.useAuthMock;
        if (environment.useAuthMock) {
            this.instance = {
                ...stubbedPublicClientApplication,
                getActiveAccount: this.getActiveAccount.bind(this),
                setActiveAccount: this.setActiveAccountFromAccount.bind(this),
                getAllAccounts: this.getAllAccounts.bind(this),
                enableAccountStorageEvents: this.enableAccountStorageEvents.bind(this)
            } as IPublicClientApplication;
        } else {
            this.instance = msalService.instance;
        }
    }

    unsubscribeToLoginEvents(customComponentName?: string) {
        const componentName = customComponentName ?? this.getCallerComponent();
        if (environment.debug) {
            console.debug(`%c Unsubscribing component ${componentName} from LoginEvents`, CommonValues.LoggingMessage_ColorBlue);
        }
        const subject = this.unsubscribeSubjects.get(componentName);
        if (subject) {
            subject.next();
            subject.complete();
        }
        this.unsubscribeSubjects.delete(componentName);
    }

    subscribeToLoginEvents(callback: (user: UsersBus) => void, customComponentName?: string) {
        if (callback) {
            const componentName = customComponentName ?? this.getCallerComponent();
            if (environment.debug) {
                console.debug(`%c Subscribing component ${componentName} to LoginEvents`, CommonValues.LoggingMessage_ColorBlue);
            }
            const compSubject = new Subject<void>();
            if (!this.unsubscribeSubjects.has(componentName)) {
                this.unsubscribeSubjects.set(componentName, compSubject);
            }
            this.userLoggedInEvent
                .pipe(takeUntil(compSubject))
                .subscribe((user: UsersBus) => {
                    callback(user);
                });
        }
    }
    protected getActiveAccount(): AccountInfo {
        if (this.useMock) {
            return mockUserAccountInfo;
        }
        return this.accountInfo;
    }
    setActiveAccount(): AccountInfo {
        if (!this.instance) {
            return null;
        }
        let activeAccount = this.instance.getActiveAccount();
        if (!activeAccount && this.instance.getAllAccounts().length > 0) {
            const accounts = this.instance.getAllAccounts();
            activeAccount = accounts[0];
        }
        if (activeAccount) {
            this.setActiveAccountFromAccount(activeAccount);
        }
        return activeAccount;
    }

    protected setActiveAccountFromAccount(accountInfo: AccountInfo) {
        this.accountInfo = accountInfo;
        this.instance.setActiveAccount(accountInfo);
    }

    protected getAllAccounts(): AccountInfo[] {
        return [mockUserAccountInfo];
    }

    protected enableAccountStorageEvents() {
        return true;
    }

    /**
     * Provides a way to force a route to check if logged in and will redirect if not. Not used very often and only in special cases
     *
     * @return {*}
     * @memberof CmProAuthService
     */
    handleRedirectObservable() {
        return this.msalService.handleRedirectObservable();
    }

    loginRedirect(request?: RedirectRequest): Observable<void> {
        if (!this.useMock) {
            return this.msalService.loginRedirect(request);
        }
        return of(null);
    }

    public clearStorage() {
        localStorage.removeItem(CommonValues.StorageKeys_UserAuth);
        localStorage.removeItem(CommonValues.StorageKeys_UserSettings);
        localStorage.removeItem(CommonValues.StorageKeys_LegacyToken);
        sessionStorage.clear();
        this.dbService.clear(CommonValues.Database_FjxRefData).subscribe();
    }

    logout(logoutRequest?: EndSessionRequest, logoutAllSessions: boolean = true): Observable<void | null> {
        this.user = defaultUser;
        this.accountInfo = mockUserAccountInfo;
        this.clearStorage();
        this.userLoggedInEvent.next(null);
        if (!this.useMock && logoutAllSessions) {
            return this.msalService.logoutRedirect(logoutRequest);
        }
        return of(null);
    }

    parseUserResponse(user: UsersBus, userMSALFlow: boolean = false, loadedFromCache: boolean = false): UsersBus {
        if (!loadedFromCache) {
            const newUser = new UsersBus(user);
            if (!userMSALFlow) {
                let tokenDecoded = this.decodeToken(newUser.token);
                newUser.setAppClaims(tokenDecoded);
                const appSettings = new AppSettings(tokenDecoded);
                localStorage.setItem(CommonValues.StorageKeys_UserSettings, JSON.stringify(appSettings));
            } else {
                //This is using Microsoft.Entra. We will fill in the token from the caller on return
                const appClaims = (user.claims ?? []).reduce((acc, v) => {
                    acc[v.type] = v.value;
                    return acc;
                }, {});
                newUser.setAppClaims(appClaims);
                const appSettings = new AppSettings();
                appSettings.setSettings(appClaims);
                localStorage.setItem(CommonValues.StorageKeys_UserSettings, JSON.stringify(appSettings));
            }
            localStorage.setItem(CommonValues.StorageKeys_UserAuth, JSON.stringify(newUser));
            this.user = newUser;
        } else {
            this.user = new UsersBus(user);
        }
        //we surface a clone of the original
        this.userLoggedInEvent.next(new UsersBus(this.user));
        return this.user;
    }

    /**
    * @deprecated This decodeToken is deprecated and will be removed in future versions.
    */
    decodeToken(token: string) {
        let decodedToken: any = null;
        if (token) {
            let jwtHelper = new JwtHelperService({
                tokenGetter: () => {
                    return token;
                }
            });
            decodedToken = jwtHelper.decodeToken();
        }
        return decodedToken;

    }

    getMsalToken(): Observable<AuthenticationResult> {
        return this.msalService.acquireTokenSilent({
            scopes: environment.authConfig.resources.apiScopes
        });
    }

    private getCallerComponent(): string {
        const error = new Error();
        const stack = error.stack?.split('\n');
        if (stack && stack.length > 3) {
            for (let i = 3; i < stack.length; i++) {
                const callerLine = stack[i].trim();
                const match = callerLine.match(/at (.*?Component)\./);
                if (match) {
                    return match[1];
                }
            }
            const callerLine = stack[3].trim();
            const match = callerLine.match(/at (.*?)\./);
            return match ? match[1] : 'UnknownComponent';
        }
        return 'UnknownComponent';
    }
}
